import React, { useEffect, useState } from "react";
import { ModalProps, RequestDetails } from "./Types";
import { getAnsweredRequest, displayNameInfo } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import DefaultAirLogo from "../../assets/images/logo.svg";
import FlightRouteArc from "./FlightRouteArc";

type ClassType = "First" | "Economy" | "Eco+" | "Business" | "NonRev";

const RequestReviewTab: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  airlineId,
  requestDetailId,
  logoImg,
  airlineName,
  flightDate,
  departureAirport,
  flightNumber,
  departureTime,
  flightDuration,
  arrivalAirport,
  arrivalTime,
  departureCity,
  arrivalCity,
}) => {
  const [requestData, setRequestData] = useState<RequestDetails | null>(null);
  const [responseName, setResponseName] = useState<any>(null);
  const [showComments, setShowComments] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    if (!isOpen || !requestDetailId) return;
    const fetchRequestData = async () => {
      showLoader();
      try {
        const response = await getAnsweredRequest(requestDetailId);
        setRequestData(response);
      } catch (err) {
        console.error("Failed to load request details", err);
      } finally {
        hideLoader();
      }
    };
    fetchRequestData();
  }, [isOpen, requestDetailId]);

  useEffect(() => {
    if (requestData?.userId) {
      const fetchDisplayName = async () => {
        try {
          const response = await displayNameInfo(requestData.userId);
          setResponseName(response);
        } catch (err) {
          console.error("Failed to load display name", err);
        }
      };
      fetchDisplayName();
    }
  }, [requestData?.userId]);

  const isOverbooked = (value: number | null): boolean => {
    return value != null && value < 0;
  };

  const getClassValue = (classLabel: ClassType): number | null => {
    switch (classLabel) {
      case "First":
        return requestData?.firstClass ?? null;
      case "Economy":
        return requestData?.eco ?? null;
      case "Eco+":
        return requestData?.ecoPlus ?? null;
      case "Business":
        return requestData?.busClass ?? null;
      default:
        return null;
    }
  };

  const isMoreSeatsAvailable = (classLabel: ClassType): boolean => {
    switch (classLabel) {
      case "First":
        return !!requestData?.moreFirstClass;
      case "Economy":
        return !!requestData?.moreEco;
      case "Eco+":
        return !!requestData?.moreEcoPlus;
      case "Business":
        return !!requestData?.moreBusClass;
      default:
        return false;
    }
  };

  const getTimeSince = (dateString?: string) => {
    if (!dateString) return "";
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 Day ago";
    return `${diffDays} Days ago`;
  };

  if (!isOpen) return null;

  const seatBoxStyle = (classLabel: ClassType) => {
    const value = getClassValue(classLabel);
    return `flex flex-col items-center justify-center h-14 w-full border rounded-xl transition-all ${
      isOverbooked(value)
        ? "border-red-400 bg-red-50 text-red-600"
        : "border-gray-200"
    }`;
  };

  return (
    <div
      id="default-modal1"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 sm:p-6"
    >
      <div className="relative w-full max-w-[440px] max-h-[calc(100vh-24px)]">
        <div className="bg-white shadow-xl rounded-2xl max-h-[calc(100vh-24px)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-[#192227] px-5 py-3 shrink-0">
            <h2 className="text-base font-semibold text-white">Answered Request</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-5 pb-4 pt-3 overflow-y-auto min-h-0 flex-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {/* Airline Card */}
            

            {/* Flight Route Card */}
            <div className="bg-gray-50 rounded-xl px-4 py-2.5 mb-2.5">
              <div className="flex items-center justify-between">
                {/* Departure */}
                <div className="text-left">
                  <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px]"></i>
                    <span>{departureTime}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{departureAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{departureCity || " "}</p>
                </div>

                {/* Route Line */}
                <FlightRouteArc duration={flightDuration} className="flex-1 mx-3" />

                {/* Arrival */}
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px] rotate-90"></i>
                    <span>{arrivalTime}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{arrivalAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{arrivalCity || " "}</p>
                </div>
              </div>
            </div>

            {/* Date / Flight Name / Flight Number */}
            <div className="grid grid-cols-3 gap-3 px-1 mb-2.5 items-center">
              {/* Date */}
              <div className="min-w-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                  Date
                </p>
                <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">
                  {flightDate}
                </p>
              </div>

              {/* Flight Name */}
              <div className="min-w-0 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide text-center">
                  Flight name
                </p>

                <div className="flex items-center justify-center gap-2 mt-0.5 h-5">
                  <img
                    className="w-5 h-5 object-contain rounded shrink-0"
                    src={logoImg || DefaultAirLogo}
                    alt={airlineName || "Airline"}
                    onError={(e) => {
                      e.currentTarget.src = "/assets/logos/air-logo.png";
                    }}
                  />

                  <p className="text-sm font-medium text-gray-800 truncate">
                    {airlineName}
                  </p>
                </div>
              </div>

              {/* Flight Number */}
              <div className="text-right min-w-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                  Flight no
                </p>
                <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">
                  {flightNumber}
                </p>
              </div>
            </div>

            {/* Seat Class Values */}
            <div className="grid grid-cols-4 gap-2 mb-2.5">
              {(["First", "Economy", "Eco+", "Business"] as ClassType[]).map((label) => (
                <div
                  key={label}
                  className={seatBoxStyle(label)}
                >
                  <p className={`font-semibold text-base ${isOverbooked(getClassValue(label)) ? "text-red-600" : "text-gray-800"}`}>
                    {getClassValue(label) ?? 0}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {label === "Business" ? "Bus" : label === "Economy" ? "Eco" : label}
                  </p>
                </div>
              ))}
            </div>

            

            {/* Listed Passengers */}
            <div className="flex flex-col items-center mb-2.5">
              <p className="text-sm font-medium text-gray-700 mb-2">Listed Passengers</p>
              <div className="flex flex-col items-center justify-center h-12 w-24 border border-gray-200 rounded-xl">
                <p className="font-semibold text-base text-gray-800">
                  {requestData?.nonRevListed ?? 0}
                </p>
                <p className="text-[10px] text-gray-400">Non Rev</p>
              </div>
            </div>

            {/* Non-Rev Pax Per Class */}
            {((requestData?.nonFirstClass ?? 0) > 0 ||
              (requestData?.nonEco ?? 0) > 0 ||
              (requestData?.nonEcoPlus ?? 0) > 0 ||
              (requestData?.nonBusClass ?? 0) > 0) && (
              <div className="border border-dashed border-gray-300 rounded-xl px-4 py-2.5 mb-2.5">
                <p className="text-sm font-medium text-gray-700 mb-2 text-center">
                  Listed Non-Rev Pax Per Class
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "First", value: requestData?.nonFirstClass },
                    { label: "Eco", value: requestData?.nonEco },
                    { label: "Eco+", value: requestData?.nonEcoPlus },
                    { label: "Bus", value: requestData?.nonBusClass },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center justify-center h-12 border border-gray-200 rounded-xl"
                    >
                      <p className="font-semibold text-base text-gray-800">{item.value ?? 0}</p>
                      <p className="text-[10px] text-gray-400">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Class-level notes (overbooked / no class / more seats available) */}
            {(() => {
              const classes: ClassType[] = ["First", "Economy", "Eco+", "Business"];
              const notes: string[] = [];
              classes.forEach((cls) => {
                const value = getClassValue(cls);
                if (isOverbooked(value)) notes.push(`${cls} class is overbooked`);
                if (value === 0) notes.push(`This flight has no ${cls} class`);
                if (isMoreSeatsAvailable(cls))
                  notes.push(`More seats available in ${cls} class (count unknown)`);
              });
              if (notes.length === 0) return null;
              return (
                <div className="mb-2.5 space-y-1.5">
                  {notes.map((note, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <span className="text-[#FBBF24] mt-0.5">•</span>
                      <span>{note}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Requested By */}
            <div className="flex items-center gap-3 mb-2.5">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {responseName?.profileImage ? (
                  <img
                    src={responseName.profileImage}
                    alt="avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-400">Requested by</p>
                <p className="text-sm font-medium text-gray-800">
                  {responseName?.displayName || "Anonymous User"}
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{getTimeSince(requestData?.createdDate)}</span>
              </div>
            </div>

            {/* Comments Button */}
            <button
              type="button"
              onClick={() => setShowComments(!showComments)}
              className="w-full flex items-center justify-center gap-2 py-2 border border-gray-200 rounded-full text-sm text-gray-500 hover:bg-gray-50 transition-colors bg-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Comments
            </button>

            {/* Comment Content (expandable) */}
            {showComments && (
              <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">
                  {requestData?.comments || "No comment provided"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestReviewTab;
